import { useEffect, useState } from "react";
import api, { nomeCompleto } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Users, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function ClassiPage() {
  const { user } = useAuth();
  const [classi, setClassi] = useState([]);
  const [corsi, setCorsi] = useState([]);
  const [studenti, setStudenti] = useState([]);
  const [docenti, setDocenti] = useState([]);
  const [open, setOpen] = useState(false);
  const [editC, setEditC] = useState(null);
  const [form, setForm] = useState({ nome:"", corso_id:"", docente_id:"", docenti_ids:[], docenti_assegnazioni:[], data_inizio:"", data_fine:"", max_studenti:25, studenti_ids:[] });
  const admin = user?.role === "admin";
  const [q, setQ] = useState("");
  const canManage = admin || user?.role === "collaboratore";
  const studentiInAltre = (excludeId) => new Set(classi.filter(c => c.id !== excludeId).flatMap(c => c.studenti_ids || []));

  const load = async () => {
    try {
      const [c, cs, us1, us2] = await Promise.all([
        api.get("/classi"), api.get("/corsi"),
        canManage ? api.get("/users?role=studente") : Promise.resolve({data:[]}),
        canManage ? api.get("/users?role=docente") : Promise.resolve({data:[]}),
      ]);
      setClassi(c.data); setCorsi(cs.data); setStudenti(us1.data); setDocenti(us2.data);
    } catch { toast.error("Errore caricamento classi"); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const { data } = await api.post("/classi", { ...form, max_studenti:+form.max_studenti });
      toast.success("Classe creata");
      setOpen(false); load();
    } catch { toast.error("Errore creazione"); }
  };

  const delClasse = async (id) => {
    if (!window.confirm("Eliminare definitivamente la classe? Le lezioni associate non verranno cancellate.")) return;
    try { await api.delete(`/classi/${id}`); toast.success("Classe eliminata"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore eliminazione"); }
  };

  const toggleStudente = (id) => {
    setForm(f => ({ ...f, studenti_ids: f.studenti_ids.includes(id) ? f.studenti_ids.filter(x=>x!==id) : [...f.studenti_ids, id] }));
  };
  const toggleDocente = (id) => {
    setForm(f => ({ ...f, docenti_ids: f.docenti_ids.includes(id) ? f.docenti_ids.filter(x=>x!==id) : [...f.docenti_ids, id] }));
  };
  const saveEdit = async () => {
    try {
      const { data } = await api.put(`/classi/${editC.id}`, { ...editC, max_studenti:+editC.max_studenti });
      toast.success("Classe aggiornata");
      setEditC(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore aggiornamento"); }
  };
  // helper generici che lavorano su uno stato qualsiasi (create o edit)
  const tglStud = (f, setF, id) => setF({ ...f, studenti_ids: f.studenti_ids.includes(id) ? f.studenti_ids.filter(x=>x!==id) : [...f.studenti_ids, id] });
  const tglDoc = (f, setF, id) => {
    const has = f.docenti_ids.includes(id);
    return setF({ ...f,
      docenti_ids: has ? f.docenti_ids.filter(x=>x!==id) : [...f.docenti_ids, id],
      docenti_assegnazioni: has ? (f.docenti_assegnazioni||[]).filter(a=>a.docente_id!==id) : [...(f.docenti_assegnazioni||[]), {docente_id:id, ore_teoria:0, ore_pratica:0}] });
  };
  const setAss = (f, setF, id, field, val) => setF({ ...f, docenti_assegnazioni: (f.docenti_assegnazioni||[]).map(a => a.docente_id===id ? {...a, [field]:+val} : a) });

  return (
    <div className="p-4 sm:p-8">
      <div className="flex flex-wrap gap-4 items-center justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#F7941E] mb-2">Gestione Didattica</div>
          <h1 className="text-3xl font-bold text-stone-900">Classi</h1>
          <p className="text-stone-600 mt-1">Blended learning con BigBlueButton</p>
          <Input value={q} onChange={e=>setQ(e.target.value)} placeholder="Cerca classe..." className="mt-3 max-w-xs" data-testid="classi-search"/>
        </div>
        {canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-[#F7941E] hover:bg-[#E08316] text-white" data-testid="new-classe-btn"><Plus className="w-4 h-4 mr-2"/>Nuova Classe</Button></DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Crea nuova classe</DialogTitle><DialogDescription>Abbina corso, docenti e studenti alla classe.</DialogDescription></DialogHeader>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="col-span-2"><Label>Nome classe</Label><Input data-testid="classe-nome-input" value={form.nome} onChange={e=>setForm({...form,nome:e.target.value})}/></div>
                <div className="col-span-2">
                  <Label>Docenti abbinati ({form.docenti_ids.length})</Label>
                  <div className="border border-[#E2E8F0] rounded-lg max-h-48 overflow-y-auto p-2 space-y-1">
                    {docenti.map(d => (
                      <label key={d.id} className="flex items-center gap-2 p-2 hover:bg-[#FFF5EB] rounded cursor-pointer">
                        <input type="checkbox" checked={form.docenti_ids.includes(d.id)} onChange={()=>toggleDocente(d.id)} data-testid={`docente-check-${d.id}`}/>
                        <span className="text-sm">{nomeCompleto(d)}{d.materie?.length > 0 && <span className="text-stone-500"> · {d.materie.join(", ")}</span>}</span>
                      </label>
                    ))}
                    {docenti.length === 0 && <div className="text-sm text-stone-500 p-2">Nessun docente. Crealo da Anagrafiche.</div>}
                  </div>
                </div>
                <div><Label>Data inizio</Label><Input type="date" value={form.data_inizio} onChange={e=>setForm({...form,data_inizio:e.target.value})}/></div>
                <div><Label>Data fine</Label><Input type="date" value={form.data_fine} onChange={e=>setForm({...form,data_fine:e.target.value})}/></div>
                <div><Label>Max studenti</Label><Input type="number" value={form.max_studenti} onChange={e=>setForm({...form,max_studenti:e.target.value})}/></div>
                <div className="col-span-2">
                  <Label>Studenti iscritti ({form.studenti_ids.length})</Label>
                  <div className="text-xs text-stone-400 mb-1">Gli studenti già iscritti ad un'altra classe non sono selezionabili.</div>
                  <div className="border border-[#E2E8F0] rounded-lg max-h-40 overflow-y-auto p-2 space-y-1">
                    {studenti.filter(s => !studentiInAltre(null).has(s.id) || form.studenti_ids.includes(s.id)).map(s => (
                      <label key={s.id} className="flex items-center gap-2 p-2 hover:bg-[#FFF5EB] rounded cursor-pointer">
                        <input type="checkbox" checked={form.studenti_ids.includes(s.id)} onChange={()=>toggleStudente(s.id)}/>
                        <span className="text-sm">{nomeCompleto(s)} <span className="text-stone-500">({s.email})</span></span>
                      </label>
                    ))}
                    {studenti.length === 0 && <div className="text-sm text-stone-500 p-2">Nessuno studente. Aggiungili da Anagrafiche.</div>}
                  </div>
                </div>
              </div>
              <DialogFooter><Button onClick={save} className="bg-[#F7941E] hover:bg-[#E08316] text-white" data-testid="classe-save-btn">Salva Classe</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {classi.filter(c=>!q || (c.nome||"").toLowerCase().includes(q.toLowerCase())).map(c => {
          const corso = corsi.find(x => x.id === c.corso_id);
          return (
            <Card key={c.id} className="p-5 bg-white border-[#E2E8F0]" data-testid={`classe-card-${c.id}`}>
              <div className="flex justify-between items-start">
                <div className="font-bold text-stone-900 text-lg mb-1">{c.nome}</div>
                {canManage && (
                  <div className="flex">
                    <Button variant="ghost" size="sm" onClick={()=>setEditC({...c, docenti_ids: c.docenti_ids||[], studenti_ids: c.studenti_ids||[], docenti_assegnazioni: c.docenti_assegnazioni||[]})} data-testid={`edit-classe-${c.id}`}><Pencil className="w-4 h-4 text-[#F7941E]"/></Button>
                    {admin && <Button variant="ghost" size="sm" onClick={()=>delClasse(c.id)} data-testid={`del-classe-${c.id}`}><Trash2 className="w-4 h-4 text-red-500"/></Button>}
                  </div>
                )}
              </div>
              <div className="text-sm text-[#F7941E] font-medium mb-3">{corso?.titolo || "-"}</div>
              <div className="text-xs text-stone-500 space-y-1">
                <div>Periodo: {c.data_inizio} → {c.data_fine}</div>
                <div data-testid={`classe-docenti-${c.id}`}>Docenti: <span className="text-stone-900">{c.docenti_dettagli?.map(d=>d.nome).join(", ") || c.docente_nome || "-"}</span></div>
                <div className="flex items-center gap-1"><Users className="w-3 h-3"/> {c.studenti_ids?.length || 0} / {c.max_studenti} studenti</div>
                <div>Docenti: {(c.docenti_ids?.length || 0) + (c.docente_id ? 1 : 0)} abbinati</div>
                {c.studenti_nomi?.length > 0 && (
                  <div className="mt-1 pt-1 border-t border-[#E2E8F0]">
                    <div className="font-semibold text-stone-700 mb-0.5">Studenti:</div>
                    <div>{c.studenti_nomi.join(" · ")}</div>
                  </div>
                )}
              </div>
            </Card>
          );
        })}
        {classi.length === 0 && <div className="col-span-full text-center text-stone-500 py-12">Nessuna classe attiva.</div>}
      </div>

      <Dialog open={!!editC} onOpenChange={(o)=>{if(!o) setEditC(null);}}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Modifica classe</DialogTitle><DialogDescription>Modifica alunni, docenti con ore e date della classe.</DialogDescription></DialogHeader>
          {editC && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="col-span-2"><Label>Nome classe</Label><Input data-testid="edit-classe-nome" value={editC.nome} onChange={e=>setEditC({...editC,nome:e.target.value})}/></div>
            <div><Label>Data inizio</Label><Input type="date" value={editC.data_inizio} onChange={e=>setEditC({...editC,data_inizio:e.target.value})}/></div>
            <div><Label>Data fine</Label><Input type="date" value={editC.data_fine} onChange={e=>setEditC({...editC,data_fine:e.target.value})}/></div>
            <div><Label>Max studenti</Label><Input type="number" value={editC.max_studenti} onChange={e=>setEditC({...editC,max_studenti:e.target.value})}/></div>
            <div className="col-span-2"><Label>Docenti abbinati ({(editC.docenti_ids||[]).length})</Label>
              <div className="border border-[#E2E8F0] rounded-lg max-h-48 overflow-y-auto p-2 space-y-1">
                {docenti.map(d => (
                  <label key={d.id} className="flex items-center gap-2 p-2 hover:bg-[#FFF5EB] rounded cursor-pointer">
                    <input type="checkbox" checked={(editC.docenti_ids||[]).includes(d.id)} onChange={()=>setEditC(prev=>{ const has=(prev.docenti_ids||[]).includes(d.id); return {...prev, docenti_ids: has ? prev.docenti_ids.filter(x=>x!==d.id) : [...(prev.docenti_ids||[]), d.id]}; })} data-testid={`edit-docente-check-${d.id}`}/>
                    <span className="text-sm">{nomeCompleto(d)}{d.materie?.length > 0 && <span className="text-stone-500"> · {d.materie.join(", ")}</span>}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="col-span-2"><Label>Studenti iscritti ({(editC.studenti_ids||[]).length})</Label>
              <div className="text-xs text-stone-400 mb-1">Gli studenti già iscritti ad un'altra classe non sono selezionabili.</div>
              <div className="border border-[#E2E8F0] rounded-lg max-h-40 overflow-y-auto p-2 space-y-1">
                {studenti.filter(s => !studentiInAltre(editC.id).has(s.id) || (editC.studenti_ids||[]).includes(s.id)).map(s => (
                  <label key={s.id} className="flex items-center gap-2 p-2 hover:bg-[#FFF5EB] rounded cursor-pointer">
                    <input type="checkbox" checked={(editC.studenti_ids||[]).includes(s.id)} onChange={()=>setEditC(prev=>({...prev, studenti_ids: (prev.studenti_ids||[]).includes(s.id) ? prev.studenti_ids.filter(x=>x!==s.id) : [...(prev.studenti_ids||[]), s.id]}))} data-testid={`edit-studente-check-${s.id}`}/>
                    <span className="text-sm">{nomeCompleto(s)} <span className="text-stone-500">({s.email})</span></span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          )}
          <DialogFooter><Button onClick={saveEdit} className="bg-[#F7941E] hover:bg-[#E08316] text-white" data-testid="edit-classe-save">Salva modifiche</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
